<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { AppDatePicker } from '@/features/App';
import { SuiIcon } from '@developers/scoro-ui';
import { useFormatter } from '@/composables';

type IntegrationsDatePickerProps = {
	name: string;
	value: string;
	label: string;
	minDate?: string | null;
	readonly?: boolean;
	isNotSynced?: boolean;
};

const props = defineProps<IntegrationsDatePickerProps>();

const { dateFormatForSUIdatePicker: dateFormat } = useFormatter();

const modelValue = ref(props.value);
const hiddenInput = ref<HTMLInputElement | null>(null);

const parseYmdDateToUtcMs = (value: string): number | null => {
	// We expect YYYY-MM-DD; parse as UTC midnight to avoid timezone-related off-by-one.
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) {
		return null;
	}

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);

	if (
		!Number.isFinite(year) ||
		!Number.isFinite(month) ||
		!Number.isFinite(day)
	) {
		return null;
	}

	return Date.UTC(year, month - 1, day);
};

const isDateDisabled = (date: string): boolean => {
	if (!props.minDate) {
		return false;
	}

	const dateMs = parseYmdDateToUtcMs(date);
	const minDateMs = parseYmdDateToUtcMs(props.minDate);

	// If format is unexpected, don't disable anything (avoid false negatives).
	if (dateMs === null || minDateMs === null) {
		return false;
	}

	return dateMs < minDateMs;
};

const syncHiddenInput = (val: string, dispatchEvent = false): void => {
	if (!hiddenInput.value) {
		return;
	}
	hiddenInput.value.value = val;
	// Trigger existing jQuery listeners that rely on .syncStartDate changes
	// Only dispatch event for user interactions, not for initial mount or prop changes
	if (dispatchEvent) {
		hiddenInput.value.dispatchEvent(new Event('change', { bubbles: true }));
	}
};

const onInput = (val: string): void => {
	const previousValue = modelValue.value;

	if (val === previousValue) {
		return;
	}

	modelValue.value = val;
	syncHiddenInput(val, true);
};

watch(
	() => props.value,
	(val) => {
		modelValue.value = val;
		syncHiddenInput(val, false);
	}
);

onMounted(() => {
	syncHiddenInput(modelValue.value, false);
});
</script>

<template>
	<div
		class="integrations-date-picker"
		:class="{ 'integrations-date-picker--readonly': readonly }"
	>
		<div class="integrations-date-picker__wrapper">
			<AppDatePicker
				:model-value="modelValue"
				:label="label"
				:format="dateFormat"
				:disabled="readonly"
				:date-disabled="isDateDisabled"
				append-to-body
				size="medium"
				@update:model-value="onInput"
			/>
			<SuiIcon
				class="integrations-date-picker__icon"
				name="icon-calendar-16"
			/>
		</div>
		<input
			ref="hiddenInput"
			type="hidden"
			:name="name"
			:value="modelValue"
			class="syncStartDate"
			:class="{ 'js-not-synced': isNotSynced }"
		/>
	</div>
</template>

<style lang="scss" scoped>
.integrations-date-picker {
	max-width: 17rem;
	min-width: 14rem;

	&__wrapper {
		position: relative;

		:deep(.sui-input__field) {
			padding-right: 2.5rem;
		}
	}

	&__icon {
		position: absolute;
		right: 0.75rem;
		bottom: 1rem;
		color: var(--sui-color-neutral-50, #b4bcca);
		pointer-events: none;
	}

	&--readonly {
		.integrations-date-picker__icon {
			color: $neutral-60;
		}
	}
}
</style>

<style lang="scss">
.integrations-date-picker.integrations-date-picker--readonly {
	.sui-input.sui-input--disabled {
		color: $neutral-90;
		-webkit-text-fill-color: $neutral-90;
		opacity: 1;
		background-color: $neutral-10;
	}

	.sui-input.sui-input--disabled input,
	.sui-input.sui-input--disabled input:disabled,
	.sui-input.sui-input--disabled input[type='text'],
	.sui-input.sui-input--disabled input[type='text']:disabled {
		color: $neutral-90;
		-webkit-text-fill-color: $neutral-90;
		opacity: 1;
		background-color: $neutral-10;
	}

	.sui-input .sui-input__placeholder-label {
		color: $neutral-90;
		-webkit-text-fill-color: $neutral-90;
		opacity: 1;
	}
}

.sui-date-picker .mx-datepicker-main.mx-datepicker-popup,
.integrations-date-picker .mx-datepicker-main.mx-datepicker-popup {
	.mx-table.mx-table-date {
		width: 100%;
		table-layout: fixed;

		td.cell {
			text-align: center;

			div {
				margin-left: -6px;
				margin-right: auto;
			}
		}

		td.mx-week-number,
		th:first-child {
			width: 3rem;
		}
	}
}
</style>
